import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { FiLayout, FiSliders, FiTool } from 'react-icons/fi'
import {
  MdAttachMoney,
  MdBarChart,
  MdChatBubbleOutline,
  MdChecklist,
  MdGridView,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdManageAccounts,
  MdMenuBook,
  MdNewspaper,
  MdOpenInNew,
  MdOutlineCalendarToday,
  MdOutlinePeople,
  MdOutlineSettings,
  MdOutlineShare,
  MdOutlineStarBorder,
  MdOutlineTextSnippet,
  MdOutlineTrendingUp,
  MdOutlineVideocam,
  MdOutlineWarningAmber,
  MdPhonelinkSetup,
  MdSmartphone,
} from 'react-icons/md'
import { Link } from 'react-router-dom'

import type { ICategory } from './types'
import type { ReactElement } from 'react'

import Category from './Category'

import myPackage from '../../../package.json'
import SettingModal from '../SettingModal'

import MwMLogoSmall from '~/assets/mwm-owner-logo.png'
import ChangeLogModal from '~/components/ChangeLogModal'
import { AUTHORIZATIONS, COOKIES, LOCAL_STORAGE } from '~/const'
import CHANGELOG from '~/const/changelog.json'
import { FEEDBACKS_NOTION_PAGE_URL } from '~/const/feedbacks'
import { useGetMeAsUserQuery } from '~/queries/users'
import dayjs from '~/utils/dayjs'
import { formatNotionUrl } from '~/utils/notion'
import { getAllRoutesFromNavbarCategories } from '~/utils/routes'
import { isTokenValid } from '~/utils/token'

import './styles.scss'

interface Props {
  className?: string
}

const NEW_FEATURE_PATH_NAMES: string[] = []

const MWM_USERS_URL = '/mwm-users'

const CATALOG_URL = '/catalog/procolor'

const REVIEWS_URL = '/reviews'

const changelog: Record<string, string> = CHANGELOG

const currentAppVersion = myPackage.version?.split('.').splice(0, 2).join('.')

const pageId = formatNotionUrl(changelog[currentAppVersion])

const NavBar = ({ className }: Props): ReactElement => {
  /** Local state */

  const [cookies] = useCookies([COOKIES.TOKENS, COOKIES.CHANGELOG, COOKIES.NEW_VERSION_START_TIME])

  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem(LOCAL_STORAGE.NAVBAR_COLLAPSE_STATE)
      ? localStorage.getItem(LOCAL_STORAGE.NAVBAR_COLLAPSE_STATE) === 'true'
      : false
  )

  const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false)

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

  const [hoveredItems, setHoveredItems] = useState<string[]>([])

  const displayedNewValue = useCallback(
    (name: string) =>
      !(dayjs(new Date()) > dayjs(cookies[COOKIES.NEW_VERSION_START_TIME]).add(7, 'day')) &&
      NEW_FEATURE_PATH_NAMES.includes(name),
    [cookies]
  )

  const categories = useMemo((): ICategory[] => {
    const commonIconProps = {
      size: 18,
    }

    return [
      {
        links: [
          {
            icon: <MdOutlineTextSnippet {...commonIconProps} />,
            name: 'Summary',
            url: '/summary',
          },
          {
            authorization: AUTHORIZATIONS.ANALYTICS.BUSINESS_REPORTS.READ,
            icon: <MdBarChart {...commonIconProps} />,
            name: 'Reporting',
            submenu: [
              {
                authorization: AUTHORIZATIONS.ANALYTICS.BUSINESS_REPORTS.READ,
                name: 'Overview',
                url: 'analytics/business-report/overview',
              },
              {
                authorization: AUTHORIZATIONS.ANALYTICS.BUSINESS_REPORTS.READ,
                name: 'Sandbox',
                url: '/analytics/business-report/sandbox',
              },
            ],
            url: '/analytics/business-report/overview',
          },
          {
            icon: <MdOutlineTrendingUp {...commonIconProps} />,
            name: 'App Store Optimization',
            submenu: [
              { name: 'Overview', url: '/aso/overview' },
              { name: 'Advanced', url: '/aso/advanced' },
            ],
            url: '/aso/overview',
          },
          {
            authorization: AUTHORIZATIONS.VERSION.CLUSTER.READ,
            icon: <MdOutlineShare {...commonIconProps} />,
            name: 'Competitors',
            submenu: [
              {
                isNewFeature: true,
                name: 'Clusters',
                url: 'https://mwm-intranet-frontend-1058312231336.europe-west1.run.app/clusters',
              },
              { name: 'Companies', url: '/competitors/companies' },
              { name: 'Recommendations', url: '/competitors/recommendations' },
            ],
            url: '/competitors/clusters',
          },
          {
            authorization: AUTHORIZATIONS.ANALYTICS.UA_DASHBOARD.READ,
            icon: <FiLayout {...commonIconProps} />,
            name: 'UA Dashboard',
            url: '/ua-dashboard',
          },
        ],
        title: 'Analyze',
      },
      {
        links: [
          {
            icon: <MdChecklist {...commonIconProps} />,
            name: 'Todolists',
            submenu: [
              {
                authorization: AUTHORIZATIONS.ASSETS_MANAGER.ASSET.READ,
                name: 'List',
                url: '/todolists',
              },
              {
                authorization: AUTHORIZATIONS.DYNAMIC_SCREENS.PLACEMENT.READ,
                name: 'Templates',
                url: '/todolist-templates',
              },
            ],
            url: '/todolists',
          },
          {
            icon: <MdChecklist {...commonIconProps} />,
            name: 'Checklists',
            url: '/checklists',
          },
          {
            icon: <MdAttachMoney {...commonIconProps} />,
            name: 'In-App Purchases',
            submenu: [
              { name: 'Subscriptions', url: '/in-app-purchases/subscriptions' },
              { name: 'Products', url: '/in-app-purchases/products' },
              { name: 'Graph', url: '/in-app-purchases/graph' },
              {
                authorization: AUTHORIZATIONS.IAP.V2.READ,
                isNewFeature: true,
                name: 'In-apps',
                url: '/in-app-purchases/in-apps',
              },
              {
                authorization: AUTHORIZATIONS.IAP.V2.READ,
                isNewFeature: true,
                name: 'Offers',
                url: '/in-app-purchases/offers',
              },
              {
                authorization: AUTHORIZATIONS.IAP.V2.READ,
                isNewFeature: true,
                name: 'Features Group',
                url: '/in-app-purchases/feature-groups',
              },
              {
                authorization: AUTHORIZATIONS.IAP.V2.READ,
                isNewFeature: true,
                name: 'Features',
                url: '/in-app-purchases/features',
              },
            ],
            url: '/in-app-purchases/subscriptions',
          },
          {
            icon: <MdOpenInNew {...commonIconProps} />,
            name: 'External links',
            url: '/external-links',
          },
          {
            authorization: AUTHORIZATIONS.UA_MANAGEMENT.READ,
            icon: <MdOpenInNew {...commonIconProps} />,
            name: 'UA Management',
            url: '/ua-management',
          },
        ],
        title: 'Build',
      },
      {
        links: [
          {
            authorization: AUTHORIZATIONS.CCC.REPORTS.READ,
            icon: <MdOutlineWarningAmber {...commonIconProps} />,
            name: 'Moderation',
            url: '/reports',
          },
          {
            authorization: AUTHORIZATIONS.CCC.CATALOG.READ,
            icon: <MdMenuBook {...commonIconProps} />,
            name: 'Catalog',
            submenu: [
              {
                authorization: AUTHORIZATIONS.CCC.CATALOG.READ,
                name: 'Categories',
                url: `${CATALOG_URL}/categories`,
              },
              {
                authorization: AUTHORIZATIONS.CCC.CATALOG.READ,
                name: 'Explore',
                url: `${CATALOG_URL}/explore`,
              },
              {
                authorization: AUTHORIZATIONS.CCC.CATALOG.READ,
                name: 'Create template',
                url: `${CATALOG_URL}/create-template`,
              },
              {
                authorization: AUTHORIZATIONS.CCC.CATALOG.READ,
                name: 'Daily',
                url: `${CATALOG_URL}/daily`,
              },
            ],
            url: `${CATALOG_URL}/categories`,
          },
        ],
        title: 'Community',
      },
      {
        links: [
          {
            authorization: AUTHORIZATIONS.MWM_USERS.ACCOUNT.READ,
            icon: <MdManageAccounts {...commonIconProps} />,
            name: 'Accounts',
            url: '/accounts',
          },
          {
            authorization: AUTHORIZATIONS.MWM_USERS.ACCOUNT.READ,
            icon: <MdOutlineSettings {...commonIconProps} />,
            name: 'Social login config',
            url: '/app-config',
          },
          {
            authorization: AUTHORIZATIONS.VERSION.APPLICATION.READ,
            icon: <MdSmartphone {...commonIconProps} />,
            name: 'Applications',
            url: '/apps-management',
          },
          {
            authorization: AUTHORIZATIONS.VERSION.VIDEO.READ,
            icon: <MdOutlineTextSnippet {...commonIconProps} />,
            name: 'My files',
            url: '/my-files',
          },
        ],
        title: 'Core',
      },
      {
        links: [
          {
            icon: <MdGridView {...commonIconProps} />,
            name: 'Versions',
            url: '/versions',
          },
          {
            icon: <FiTool {...commonIconProps} />,
            name: 'Builds',
            url: '/builds',
          },
          {
            authorization: AUTHORIZATIONS.VERSION.EVENT.READ,
            icon: <MdOutlineCalendarToday {...commonIconProps} />,
            name: 'Events',
            url: '/events',
          },
          {
            authorization: AUTHORIZATIONS.REVIEWS.READ,
            icon: <MdOutlineStarBorder {...commonIconProps} />,
            name: 'Ratings & reviews',
            submenu: [
              {
                authorization: AUTHORIZATIONS.REVIEWS.READ,
                isNewFeature: displayedNewValue('reviews-monitoring'),
                name: 'Monitoring',
                url: `${REVIEWS_URL}/monitoring`,
              },
              {
                authorization: AUTHORIZATIONS.REVIEWS.READ,
                isNewFeature: displayedNewValue('reviews-management'),
                name: 'Management',
                submenu: [
                  {
                    authorization: AUTHORIZATIONS.REVIEWS.READ,
                    name: 'Reporting macros',
                    url: `${REVIEWS_URL}/management/reporting-macros`,
                  },
                  {
                    authorization: AUTHORIZATIONS.REVIEWS.READ,
                    name: 'Reply macros',
                    url: `${REVIEWS_URL}/management/reply-macros`,
                  },
                  {
                    authorization: AUTHORIZATIONS.REVIEWS.READ,
                    name: 'Topics',
                    url: `${REVIEWS_URL}/management/topics-management`,
                  },
                ],
                url: `${REVIEWS_URL}/management/reporting-macros`,
              },
            ],
            url: `${REVIEWS_URL}/monitoring`,
          },
        ],
        title: 'Monitor',
      },
      {
        links: [
          {
            authorization: AUTHORIZATIONS.AUTH.USERS.READ,
            icon: <MdOutlinePeople {...commonIconProps} />,
            isNewFeature: displayedNewValue('users'),
            name: 'IAM',
            submenu: [
              {
                isNewFeature: displayedNewValue('users'),
                name: 'Users',
                url: `${MWM_USERS_URL}/users`,
              },
              {
                authorization: AUTHORIZATIONS.AUTH.GROUPS.READ,
                name: 'Groups',
                url: `${MWM_USERS_URL}/groups`,
              },
              {
                authorization: AUTHORIZATIONS.AUTH.ROLES.READ,
                name: 'Roles',
                url: `${MWM_USERS_URL}/roles`,
              },
              {
                authorization: AUTHORIZATIONS.AUTH.AUTHORIZATIONS.READ,
                name: 'Authorizations',
                url: `${MWM_USERS_URL}/authorizations`,
              },
            ],
            url: `${MWM_USERS_URL}/users`,
          },
          {
            authorization: AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.READ,
            icon: <MdOutlineTrendingUp {...commonIconProps} />,
            name: 'Chart configs',
            secondaryUrl: '/analytics/ca/builder',
            url: '/chart-configs',
          },
          {
            authorization: AUTHORIZATIONS.APP_SETUP.TASK.READ,
            icon: <MdPhonelinkSetup {...commonIconProps} />,
            name: 'App setup',
            url: '/apps-setup',
          },
        ],
        title: 'Admin',
      },
      {
        links: [
          {
            icon: <MdMenuBook {...commonIconProps} />,
            name: 'Glossary',
            url: '/glossary',
          },
          {
            authorization: AUTHORIZATIONS.VERSION.VIDEO.READ,
            icon: <MdOutlineVideocam {...commonIconProps} />,
            name: 'Videos',
            url: '/videos',
          },
          {
            icon: <MdNewspaper {...commonIconProps} />,
            name: `What's new?`,
            url: '/releases',
          },
          {
            icon: <MdChatBubbleOutline {...commonIconProps} />,
            name: 'Feedbacks',
            url: FEEDBACKS_NOTION_PAGE_URL,
          },
        ],
        title: 'Knowledge',
      },
    ]
  }, [displayedNewValue])

  const settingModalViewPathOptions = getAllRoutesFromNavbarCategories(categories).map(({ name, url }) => ({
    label: name,
    value: url,
  }))

  /** Queries & Mutations */

  const { data: user } = useGetMeAsUserQuery(isTokenValid(cookies[COOKIES.TOKENS]))

  /** Effects */

  useEffect(() => {
    const rootElement = document.getElementById('root')

    if (rootElement) {
      if (isCollapsed) {
        rootElement.classList.add('navbar-collapsed')
      } else {
        rootElement.classList.remove('navbar-collapsed')
      }
    }
  }, [isCollapsed])

  /** Handlers */

  const handleCollapseNavbar = () =>
    setIsCollapsed((prevState) => {
      localStorage.setItem(LOCAL_STORAGE.NAVBAR_COLLAPSE_STATE, `${!prevState}`)
      return !prevState
    })

  const handleChangelogModalState = (value: boolean) => () => setIsChangelogModalOpen(value)

  const handleSettingsModalState = (value: boolean) => () => setIsSettingsModalOpen(value)

  const handleHoveringItem = (value: string, level: number) => {
    const newHoveredItems = [...hoveredItems]
    newHoveredItems[level] = value
    setHoveredItems(newHoveredItems)
  }

  /** Render */

  return (
    <>
      <div className={classNames('nav-bar-component', className, { 'navbar-collapsed': isCollapsed })}>
        <div className="header">
          <div className="collapse-button" onClick={handleCollapseNavbar}>
            {isCollapsed ? <MdKeyboardArrowRight size={24} /> : <MdKeyboardArrowLeft size={24} />}
          </div>
          <div className="title">
            <Link to="/">
              {isCollapsed ? (
                <div className="logo-container small">
                  <img alt="logo" className="logo-img" src={MwMLogoSmall} />
                </div>
              ) : (
                <div className="logo-container">
                  <img alt="logo" className="logo-img" src={MwMLogoSmall} />
                  <span className="logo-title">
                    <span className="logo-title--bold">MWM</span> Intranet
                  </span>
                </div>
              )}
            </Link>
            <button
              className={classNames('settings-icon__container', { collapsed: isCollapsed })}
              onClick={handleSettingsModalState(true)}
            >
              <FiSliders color="#aeaeae" name="sliders" size={16} />
            </button>
          </div>
        </div>
        <div className="menu">
          {categories.map((cat, index) => (
            <Category
              authorizations={user?.authorizations || []}
              category={cat}
              hoveredItems={hoveredItems}
              isCollapsed={isCollapsed}
              key={`category-${index}`}
              onHover={handleHoveringItem}
            />
          ))}
        </div>
      </div>
      <ChangeLogModal
        isOpen={isChangelogModalOpen}
        onClose={handleChangelogModalState(false)}
        pageId={pageId}
        version={myPackage.version}
      />
      <SettingModal
        isOpen={isSettingsModalOpen}
        onClose={handleSettingsModalState(false)}
        viewPathOptions={settingModalViewPathOptions}
      />
    </>
  )
}

export default NavBar
